'use client';

import posthog from 'posthog-js';
import { useCallback, useMemo } from 'react';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useCurrentWalletAdapter from '../useCurrentWalletAdapter';
import { dlog } from '../../dev';
import type { WebCaptureEvent } from 'src/providers/posthog/posthogConfig';

const DEV_LOG_POST_HOG_EVENTS = false;

const usePostHogCapture = () => {
	const walletAdapter = useCurrentWalletAdapter();
	const isE2eWallet = walletAdapter.name === Env.E2E_WALLET.name;
	const posthogEnabled = useVelocityStore((s) => s.postHog.enabled);

	// Cast required: useCallback cannot infer overloaded function types.
	// The inner cast types the implementation; the outer annotation ensures
	// the hook's return type matches WebCaptureEvent.
	const captureEvent: WebCaptureEvent = useCallback(
		((event: string, properties?: Record<string, unknown>) => {
			if (isE2eWallet || !posthogEnabled) {
				return;
			}

			if (DEV_LOG_POST_HOG_EVENTS) {
				dlog(`posthog`, `dev_capture_event: ${event}::`, properties);
			}

			dlog(`posthog`, `capture_event: ${event}`);

			posthog.capture(event, properties);
		}) as WebCaptureEvent,
		[isE2eWallet, posthogEnabled]
	);

	return useMemo(() => ({ captureEvent }), [captureEvent]);
};

export type PostHogCapture = ReturnType<
	typeof usePostHogCapture
>['captureEvent'];

export default usePostHogCapture;
